// A rental's terms, as the forms offer them and the listing shows them
// (rentals, step 3, 2026-09-24). The words stored are in lib/enum-columns.ts;
// the database refuses any other.

import { FURNISHED, LEASE_TERMS, RENT_INCLUDES } from '@/lib/enum-columns';

export type LeaseTerm = (typeof LEASE_TERMS)[number];
export type Furnished = (typeof FURNISHED)[number];
export type RentIncluded = (typeof RENT_INCLUDES)[number];

type Label = { en: string; fr: string };

export const LEASE_TERM_LABELS: Record<LeaseTerm, Label> = {
  '12_months': { en: '12 months', fr: '12 mois' },
  '6_months': { en: '6 months', fr: '6 mois' },
  month_to_month: { en: 'Month to month', fr: 'Au mois' },
  short_term: { en: 'Short term', fr: 'Court terme' },
  negotiable: { en: 'Negotiable', fr: 'À discuter' },
};

export const FURNISHED_LABELS: Record<Furnished, Label> = {
  furnished: { en: 'Furnished', fr: 'Meublé' },
  semi: { en: 'Semi-furnished', fr: 'Semi-meublé' },
  unfurnished: { en: 'Unfurnished', fr: 'Non meublé' },
};

// Quebec ads say "chauffé, éclairé, eau chaude": the words renters look for.
export const RENT_INCLUDES_LABELS: Record<RentIncluded, Label> = {
  heating: { en: 'Heating', fr: 'Chauffé' },
  electricity: { en: 'Electricity', fr: 'Éclairé' },
  hot_water: { en: 'Hot water', fr: 'Eau chaude' },
  water: { en: 'Water', fr: 'Eau' },
  internet: { en: 'Internet', fr: 'Internet' },
  appliances: { en: 'Appliances', fr: 'Électroménagers' },
  parking: { en: 'Parking', fr: 'Stationnement' },
};

export const label = (l: Label, lang: string) => (lang === 'fr' ? l.fr : l.en);

/** A monthly rent: from $100, up to $100,000 (a sale starts at $1,000). */
export const RENT_MIN = 100;
export const RENT_MAX = 100_000;

/**
 * What the law of a province says about a deposit, where we know it
 * (rentals research, 2026-09-24). Quebec forbids any deposit - the database
 * refuses one (properties_deposit_check) and the forms do not ask.
 */
export const DEPOSIT_RULES: Partial<Record<string, Label>> = {
  QC: {
    en: 'In Quebec a landlord cannot ask for a deposit.',
    fr: 'Au Québec, le propriétaire ne peut pas exiger de dépôt.',
  },
  ON: {
    en: 'In Ontario only a last month’s rent deposit is allowed, no more than one month’s rent.',
    fr: 'En Ontario, seul un dépôt pour le dernier mois de loyer est permis, au plus un mois de loyer.',
  },
  BC: {
    en: 'In British Columbia a security deposit is at most half a month’s rent.',
    fr: 'En Colombie-Britannique, le dépôt de garantie est d’au plus un demi-mois de loyer.',
  },
  AB: {
    en: 'In Alberta a security deposit is at most one month’s rent.',
    fr: 'En Alberta, le dépôt de garantie est d’au plus un mois de loyer.',
  },
};

/** No deposit field at all: a sale, or a rental in Quebec. */
export const depositAllowed = (listingType: unknown, province: unknown) =>
  listingType === 'rent' && String(province || '').toUpperCase() !== 'QC';

export const isLeaseTerm = (v: unknown): v is LeaseTerm => (LEASE_TERMS as readonly unknown[]).includes(v);
export const isFurnished = (v: unknown): v is Furnished => (FURNISHED as readonly unknown[]).includes(v);
export const isRentIncluded = (v: unknown): v is RentIncluded => (RENT_INCLUDES as readonly unknown[]).includes(v);

/** "sale" or "rent" from a form, a spreadsheet cell or a query string; anything else is a sale. */
export function listingTypeOf(v: unknown): 'sale' | 'rent' {
  const s = String(v ?? '').trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  return ['rent', 'rental', 'lease', 'for rent', 'location', 'a louer', 'louer', 'loyer'].includes(s) ? 'rent' : 'sale';
}

// ---- Showing a listing (rentals, step 4) ---------------------------------

/** A rental: its price is a month's rent. */
export const isRental = (listing: { listing_type?: unknown } | null | undefined) => listing?.listing_type === 'rent';

/** "/month" after a rent: "$2,400/month", "2 400 $/mois". */
export const perMonth = (lang: string) => (lang === 'fr' ? '/mois' : '/month');

/**
 * "October 1, 2026" / "1 octobre 2026" for a date kept as written
 * (YYYY-MM-DD), or "Now" once it has passed. Read in UTC: a DATE put through
 * the server's time zone can move a day.
 */
export function availableFromLabel(value: unknown, lang: string, now = Date.now()): string | null {
  const s = value instanceof Date ? value.toISOString().slice(0, 10) : String(value ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const at = Date.parse(`${s}T00:00:00Z`);
  if (!Number.isFinite(at)) return null;
  if (at <= now) return lang === 'fr' ? 'Maintenant' : 'Now';
  return new Intl.DateTimeFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(at);
}

/** The label over a private landlord's listing, and what it means. */
export const BY_OWNER_RENTAL: Label = { en: 'For rent by owner', fr: 'À louer par le propriétaire' };
export const BY_OWNER_RENTAL_EXPLANATION: Label = {
  en: 'This home is advertised by its owner. No real estate broker is involved: you deal with the landlord directly.',
  fr: "Ce logement est annoncé par son propriétaire. Aucun courtier immobilier n'intervient : vous traitez directement avec le propriétaire.",
};

/**
 * Rental fraud is the common one: a copied ad, a landlord "abroad", money
 * asked before a visit. Said on every rental, in plain words.
 */
export const RENTAL_SAFETY: { title: Label; points: Label[] } = {
  title: { en: 'Before you pay anything', fr: 'Avant de payer quoi que ce soit' },
  points: [
    { en: 'Visit the home and meet the landlord before you send any money.', fr: 'Visitez le logement et rencontrez le propriétaire avant d’envoyer de l’argent.' },
    { en: 'Never pay by wire transfer, gift card or cryptocurrency, and be wary of a rent far below the neighbourhood.', fr: 'Ne payez jamais par virement international, carte-cadeau ou cryptomonnaie, et méfiez-vous d’un loyer bien plus bas que dans le quartier.' },
    { en: 'Get a written lease. In Quebec the lease is the Tribunal administratif du logement form, and no deposit can be asked.', fr: 'Exigez un bail écrit. Au Québec, le bail est le formulaire du Tribunal administratif du logement, et aucun dépôt ne peut être exigé.' },
  ],
};

/** A stored word's label, or null for a word the lists do not have. */
export const leaseTermLabel = (v: unknown, lang: string) => (isLeaseTerm(v) ? label(LEASE_TERM_LABELS[v], lang) : null);
export const furnishedLabel = (v: unknown, lang: string) => (isFurnished(v) ? label(FURNISHED_LABELS[v], lang) : null);
export const rentIncludedLabel = (v: unknown, lang: string) => (isRentIncluded(v) ? label(RENT_INCLUDES_LABELS[v], lang) : null);

/**
 * "Free by" choices for the filters: now, then the first of each of the next
 * six months - the day leases start (July 1 in Quebec).
 */
export function availableByOptions(lang: string, today = new Date()): { value: string; label: string }[] {
  const ymd = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const out = [{ value: ymd(today.getFullYear(), today.getMonth(), today.getDate()), label: lang === 'fr' ? 'Maintenant' : 'Now' }];
  for (let k = 1; k <= 6; k++) {
    const d = new Date(today.getFullYear(), today.getMonth() + k, 1);
    const value = ymd(d.getFullYear(), d.getMonth(), 1);
    const when = availableFromLabel(value, lang, 0);
    out.push({ value, label: lang === 'fr' ? `D’ici le ${when}` : `By ${when}` });
  }
  return out;
}
